<script setup>
/* eslint semi: 1 */

import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { confirmSignUp as AmplifyConfirmSignUp } from 'aws-amplify/auth';

import FormInput from '@/components/common/form/form-input-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';

// Mixin imports
import AuthMixin from '@/components/mixins/auth-mixin';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

// Component Options added so we can get access to mixins inside <script setup>
defineOptions({
  mixins: [AuthMixin]
});

const props = defineProps({
  email: {
    type: String,
    default: '',
    required: true
  },
  stage: {
    type: String,
    default: '',
    required: true
  },
  isSignUpMini: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['successSubmit', 'errorSubmit']);

const schema = yup.object({
  verificationCode: yup.string()
    .transform((value) => value?.trim())
    .required('label.codeIsRequired'),
});

const {
  errors: formErrors,
  handleSubmit,
  isSubmitting,
  defineField
} = useForm({
  validationSchema: toTypedSchema(schema)
});

const [verificationCode] = defineField('verificationCode');

const performConfirmUser = async (code) => {
  if (!props.email) {
    return { error: 'No email provided', success: false };
  }

  try {
    await useCognitoAuthStore().configureWithEmail(props.email);
    await AmplifyConfirmSignUp({
      username: props.email.trim().toLocaleLowerCase(),
      confirmationCode: code
    });
  } catch (error) {
    console.error('Error confirming user:', error);
    return { error, success: false };
  }

  return { error: null, success: true };
};

const handleSubmitSuccess = async (values, actions) => {
  const { error } = await performConfirmUser(values.verificationCode);
  if (error) {
    actions.setFieldError('verificationCode', 'errors.invalid.verificationCode');

    emit('errorSubmit', error);
    return;
  }

  emit('successSubmit', { verificationCode: values.verificationCode });
};

const onSubmit = handleSubmit(handleSubmitSuccess);
</script>

<template>
  <form
    class="flex flex-col flex-1 w-full justify-between"
    data-component-element-id="sign-up-verification-form"
    @submit="onSubmit"
  >
    <div />

    <div class="max-[1919px]:h-24 min-[1920px]:h-36 flex flex-col justify-between gap-4 px-3 mb-8">
      <label class="max-[1919px]:text-xs min-[1920px]:text-base w-full text-center text-white">
        {{ $t('label.pleaseCheckYourEmailForVerificationCode') }}
      </label>

      <form-input
        v-model="verificationCode"
        id="verification-code"
        data-component-element-id="sign-up-verification-code"
        type="text"
        inputTheme="light"
        required
        :disabled="isSubmitting"
        :size="isSignUpMini ? 'xs' : 'md'"
        :label="$t('label.verificationCode')"
        :validationStatus="formErrors?.verificationCode ? 'error' : undefined"
      >
        <template
          v-if="formErrors?.verificationCode"
          #validationTransparent
        >
          {{ $t(formErrors.verificationCode) }}
        </template>
      </form-input>
    </div>

    <form-button
      type="submit"
      button-theme="light"
      data-component-element-id="sign-up-verification-form-submit-btn"
      :size="isSignUpMini ? 'xs' : 'md'"
      :disabled="isSubmitting"
      :loading="isSubmitting"
      :label="$t('label.signUp')"
      @click="onSubmit"
    />
  </form>
</template>
